import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FolderPlus, Plus, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { FolderSettingsModal } from '../../features/activity/FolderSettingsModal';
import { fetchFoldersApi } from '../../api/folders.api';
import { useI18n } from '../../hooks/useI18n';
import { classNames } from '../../utils/classNames';

export const FoldersPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState({ open: false, folder: null });

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const next = await fetchFoldersApi();
      setFolders(next);
    } catch (err) {
      setError(err.message || t('folderCreateError'));
      setFolders([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    reload();
  }, [reload]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return folders;
    return folders.filter((folder) =>
      `${folder.name} ${folder.instructions}`.toLowerCase().includes(query)
    );
  }, [folders, search]);

  return (
    <DashboardLayout
      title={t('folders')}
      actions={
        <Button onClick={() => setModal({ open: true, folder: null })}>
          <Plus size={16} />
          {t('folderNew')}
        </Button>
      }
    >
      <section className="w-full">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <p className="text-sm text-neutral-500">{t('foldersPageSubtitle')}</p>
          <div className="relative w-full sm:max-w-xs">
            <Search size={16} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('folderSearchPlaceholder')}
              className="ps-9"
            />
          </div>
        </div>

        {error ? <p className="mb-4 text-sm text-red-500">{error}</p> : null}

        {loading ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="h-36 animate-pulse rounded-2xl border border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900"
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-neutral-200 px-6 py-16 text-center dark:border-neutral-800">
            <FolderPlus className="mb-3 text-neutral-400" size={28} />
            <h2 className="text-base font-semibold text-neutral-900 dark:text-white">
              {search.trim() ? t('folderSearchEmpty') : t('folderEmpty')}
            </h2>
            <p className="mt-1 max-w-md text-sm text-neutral-500">{t('foldersPageHint')}</p>
            {!search.trim() ? (
              <Button className="mt-4" onClick={() => setModal({ open: true, folder: null })}>
                <Plus size={16} />
                {t('folderNew')}
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((folder) => (
              <button
                key={folder.id}
                type="button"
                onClick={() => navigate(`/folders/${folder.id}`)}
                className={classNames(
                  'rounded-2xl border border-neutral-200 p-4 text-start transition',
                  'hover:border-neutral-300 hover:bg-[#Fafafa] dark:border-neutral-800 dark:hover:border-neutral-700 dark:hover:bg-neutral-900'
                )}
              >
                <div className="flex items-start gap-3">
                  <span
                    className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-neutral-100 dark:bg-neutral-800"
                    style={folder.color ? { color: folder.color } : undefined}
                  >
                    <FolderPlus size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h2 className="truncate text-sm font-semibold text-neutral-950 dark:text-white">
                      {folder.name}
                    </h2>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      <FolderSettingsModal
        open={modal.open}
        folder={modal.folder}
        onClose={() => setModal({ open: false, folder: null })}
        onSaved={() => reload()}
        onDeleted={() => reload()}
      />
    </DashboardLayout>
  );
};
